import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "../../styles/crearEmpresaPopup.module.css";
import { useTranslation } from "react-i18next";


const initialFormData = {
  nombre: "",
  cif: "",
  direccion: "",
  ciudad: "",
  codigo_postal: "",
  provincia: "",
  pais: "",
  logo: "",
  personalizacion: false,
};

const CrearEmpresaPopup = ({ onClose, onCreate, company, onEdit }) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(company);
  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (company) {
      setFormData({
        nombre: company.nombre ?? "",
        cif: company.cif ?? "",
        direccion: company.direccion ?? "",
        ciudad: company.ciudad ?? "",
        codigo_postal: company.codigo_postal ?? "",
        provincia: company.provincia ?? "",
        pais: company.pais ?? "",
        logo: company.logo ?? "",
        personalizacion: Boolean(company.personalizacion),
      });
    } else {
      setFormData(initialFormData);
    }
  }, [company]);

  const textRegex = /^[a-zA-Z0-9ñÑ\sáéíóúÁÉÍÓÚ&.-]+$/;
  const addressRegex = /^[a-zA-Z0-9ñÑ\sºª.,;:¡!¿?áéíóúÁÉÍÓÚ/-]+$/;
  const countryRegex = /^[a-zA-ZñÑ\sáéíóúÁÉÍÓÚ]+$/;
  const cifRegex = /^[A-Za-z]\d{7}[A-Za-z0-9]$/;
  const postalCodeRegex = /^\d{5}$/;

  const validate = (name, value) => {
    let newErrors = { ...errors };

    switch (name) {
      case "nombre":
        if (!value || typeof value !== "string" || !value.trim()) {
          newErrors[name] = t("nameCompanyInvalid");
        } else if (!textRegex.test(value.trim())) {
          newErrors[name] = t("nameCompanyInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "ciudad":
        if (value && !textRegex.test(value)) {
          newErrors[name] = t("cityInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "provincia":
        if (value && !textRegex.test(value)) {
          newErrors[name] = t("provinceInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "direccion":
        if (value && !addressRegex.test(value)) {
          newErrors[name] = t("addressInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "pais":
        if (value && !countryRegex.test(value)) {
          newErrors[name] = t("countryInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "cif":
        if (value && !cifRegex.test(value)) {
          newErrors[name] = t("cifInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      case "codigo_postal":
        if (value && !postalCodeRegex.test(value)) {
          newErrors[name] = t("postalCodeInvalid");
        } else {
          delete newErrors[name];
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const getValidationErrors = (data) => {
    const errs = {};
    const nom = (data.nombre ?? "").trim();
    if (!nom) errs.nombre = t("nameCompanyInvalid");
    else if (!textRegex.test(nom)) errs.nombre = t("nameCompanyInvalid");
    if (data.ciudad && !textRegex.test(data.ciudad)) errs.ciudad = t("cityInvalid");
    if (data.provincia && !textRegex.test(data.provincia)) errs.provincia = t("provinceInvalid");
    if (data.direccion && !addressRegex.test(data.direccion)) errs.direccion = t("addressInvalid");
    if (data.pais && !countryRegex.test(data.pais)) errs.pais = t("countryInvalid");
    if (data.cif && !cifRegex.test(data.cif)) errs.cif = t("cifInvalid");
    if (data.codigo_postal && !postalCodeRegex.test(data.codigo_postal)) errs.codigo_postal = t("postalCodeInvalid");
    return errs;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    validate(name, newValue);
  };

  const nombreTrim = formData.nombre?.trim() ?? "";
  const isSubmitDisabled = !nombreTrim;

  const handleSubmit = () => {
    const trimmedFormData = {
      ...formData,
      nombre: nombreTrim,
      cif: (formData.cif ?? "").trim() || undefined,
      direccion: (formData.direccion ?? "").trim() || undefined,
      ciudad: (formData.ciudad ?? "").trim() || undefined,
      codigo_postal: (formData.codigo_postal ?? "").trim() || undefined,
      provincia: (formData.provincia ?? "").trim() || undefined,
      pais: (formData.pais ?? "").trim() || undefined,
    };

    const submitErrors = getValidationErrors(trimmedFormData);
    setErrors(submitErrors);

    if (Object.keys(submitErrors).length === 0 && nombreTrim) {
      if (isEditMode && onEdit) {
        onEdit({ ...trimmedFormData, id_empresa: company.id_empresa });
      } else {
        onCreate(trimmedFormData);
      }
      onClose();
    }
  };
  

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  // Añadir event listener para la tecla "Enter"
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]); // Dependencia para asegurarse de que el estado más reciente de formData sea accesible

  return ReactDOM.createPortal(
    <div className={styles.popupOverlay} onClick={handleClickOutside}>
      <div className={styles.popupContent}>
        <h2>{isEditMode ? t("editCompany") : t("crearEmpresa")}</h2>
        <label className={styles.label}>
          <span className={styles.labelLine}>{t("companyName")} <span className={styles.required}>*</span></span>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className={`${styles.input} ${errors.nombre ? styles.inputError : ""}`}
          />
          {errors.nombre && <span className={styles.error}>{errors.nombre}</span>}
        </label>
        <label className={styles.label}>
          <span className={styles.labelLine}>{t("cif")} <span className={styles.optional}>({t("optional")})</span></span>
          <input
            type="text"
            name="cif"
            value={formData.cif}
            onChange={handleChange}
            className={`${styles.input} ${errors.cif ? styles.inputError : ""}`}
          />
          {errors.cif && <span className={styles.error}>{errors.cif}</span>}
        </label>
        <label className={styles.label}>
          <span className={styles.labelLine}>{t("address")} <span className={styles.optional}>({t("optional")})</span></span>
          <input
            type="text"
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
            className={`${styles.input} ${errors.direccion ? styles.inputError : ""}`}
          />
          {errors.direccion && <span className={styles.error}>{errors.direccion}</span>}
        </label>
        <div className={styles.divInputsHorizontal}>
          <label className={styles.label}>
            <span className={styles.labelLine}>{t("city")} <span className={styles.optional}>({t("optional")})</span></span>
            <input
              type="text"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
              className={`${styles.input} ${errors.ciudad ? styles.inputError : ""}`}
            />
            {errors.ciudad && <span className={styles.error}>{errors.ciudad}</span>}
          </label>
          <label className={styles.label}>
            <span className={styles.labelLine}>{t("postalCode")} <span className={styles.optional}>({t("optional")})</span></span>
            <input
              type="text"
              name="codigo_postal"
              value={formData.codigo_postal}
              onChange={handleChange}
              className={`${styles.input} ${errors.codigo_postal ? styles.inputError : ""}`}
            />
            {errors.codigo_postal && <span className={styles.error}>{errors.codigo_postal}</span>}
          </label>
        </div>
        <div className={styles.divInputsHorizontal}>
          <label className={styles.label}>
            <span className={styles.labelLine}>{t("province")} <span className={styles.optional}>({t("optional")})</span></span>
            <input
              type="text"
              name="provincia"
              value={formData.provincia}
              onChange={handleChange}
              className={`${styles.input} ${errors.provincia ? styles.inputError : ""}`}
            />
            {errors.provincia && <span className={styles.error}>{errors.provincia}</span>}
          </label>
          <label className={styles.label}>
            <span className={styles.labelLine}>{t("country")} <span className={styles.optional}>({t("optional")})</span></span>
            <input
              type="text"
              name="pais"
              value={formData.pais}
              onChange={handleChange}
              className={`${styles.input} ${errors.pais ? styles.inputError : ""}`}
            />
            {errors.pais && <span className={styles.error}>{errors.pais}</span>}
          </label>
        </div>
        <div className={styles.popupActions}>
          <button onClick={onClose} className={styles.buttonCancel}>{t("cancel")}</button>
          <button onClick={handleSubmit} className={styles.buttonCreate} disabled={isSubmitDisabled}>{isEditMode ? t("save") : t("create")}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrearEmpresaPopup;
